import { createTheme } from '@mui/material/styles';

import {
  primaryDarkGrey,
  primaryLightGrey,
  primaryBlue,
  primaryBlack,
  primaryGrey,
  primaryWhite,
} from '@/styles/colorPalette';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    label: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    label?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    label: true;
  }
}

export const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1320,
      xl: 1440,
    },
  },
  palette: {
    text: {
      primary: primaryBlack,
      secondary: primaryDarkGrey,
    },
    primary: {
      main: primaryBlue,
      contrastText: primaryWhite,
    },
    action: {
      disabled: primaryGrey,
      disabledBackground: primaryGrey,
    },
  },
  typography: {
    fontFamily: '"Open Sans", sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: '36px',
      lineHeight: '44px',
      letterSpacing: '0.2px',
      color: primaryBlack,
    },
    h2: {
      fontWeight: 700,
      fontSize: '24px',
      lineHeight: '30px',
      letterSpacing: '0.2px',
      color: primaryBlack,
    },
    subtitle1: {
      fontWeight: 600,
      fontSize: '18px',
      lineHeight: '26px',
      letterSpacing: '0.2px',
      color: primaryBlack,
    },
    subtitle2: {
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0.2px',
      color: primaryBlack,
    },
    body1: {
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '20px',
      letterSpacing: '0.2px',
      color: primaryBlack,
    },
    caption: {
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      letterSpacing: '0.2px',
      color: primaryBlack,
    },
    label: {
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      letterSpacing: '0.2px',
      textTransform: 'uppercase',
      color: primaryBlack,
    },
  },
  components: {
    MuiContainer: {
      defaultProps: {
        disableGutters: true,
        maxWidth: false,
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'small',
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          minWidth: 0,
          fontWeight: 600,
          fontSize: '14px',
          lineHeight: '20px',
          letterSpacing: '0.2px',
          textTransform: 'none',
        },
        sizeSmall: ({ theme }) => ({
          padding: theme.spacing(0.5, 1),
        }),
        sizeMedium: ({ theme }) => ({
          padding: theme.spacing(1),
        }),
        sizeLarge: ({ theme }) => ({
          padding: theme.spacing(1.5, 1),
        }),
        contained: {
          '&.Mui-disabled': {
            color: primaryWhite,
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2),
          boxShadow: '0px 2px 15px 0px rgba(0, 0, 0, 0.05)',
        }),
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: primaryLightGrey,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.55)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: primaryLightGrey,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 'auto',
        },
        indicator: {
          height: 2,
          backgroundColor: primaryBlue,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 'auto',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '16px',
          lineHeight: '24px',
          letterSpacing: '0.2px',
          color: primaryBlack,
          borderTopLeftRadius: theme.spacing(0.5),
          borderTopRightRadius: theme.spacing(0.5),

          '&.Mui-selected': {
            color: primaryBlue,
          },
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          padding: theme.spacing(1.5, 2),
          fontWeight: 600,
          fontSize: '12px',
          lineHeight: '16px',
          letterSpacing: '0.2px',
          color: primaryWhite,
          backgroundColor: primaryBlack,
          borderRadius: theme.spacing(0.5),
          boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
          opacity: 1,
        }),
        arrow: {
          color: primaryBlack,
          opacity: 1,
        },
      },
    },
  },
});
